import { Category, DisplayCategory, toCurrency, toNumber, type GraphItem, type OperatorType, type ProductType, type SkuType } from "@client/api-services/client";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "@remix-run/node";
import { Form, json, Link, redirect, useLoaderData, useParams } from "@remix-run/react";
import { createCart, fetchSkus } from "@client/api-services/server";
import { NewCache } from "~/cache/NewCache.server";
import { CountryCurrencyCache } from "~/cache/CountryCurrencyCache.server";
import { createI18nServer } from "~/i18n.server";
import { getUserPrefs } from "~/services/user-prefs.server";
import { authenticator } from "~/services/auth.server";
import { RemixFetch } from "~/apis/remix-fetch.server";
import invariant from "invariant";
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import { LangLink } from "~/components/common/LangLink";
import { TipTapWeb } from "@elemt/tip-tap";
import { useImageHost } from "~/apis/utils";
import { CheckMarkIcon } from "~/components/icons";
import { BundleCard } from "~/components/cards/BundleCard";
import { TopupCard } from "~/components/cards/TopupCard";
import { DataCard } from "~/components/cards/DataCard";
import { cartStorage } from "~/services/cart.server";
import { logger } from "~/services/logger.server";
import { useTranslation } from "react-i18next";
import { appendLangToUrl } from "~/apis/utils.server";
import { useUrlGenerator } from "~/apis/image-path";

export const loader = async function ({ request, params }: LoaderFunctionArgs) {
	const { country: countrySlug, category: categorySlug, brand: brandSlug } = params;
	invariant(countrySlug, "countrySlug can't be empty");
	invariant(brandSlug, "brandSlug can't be empty");
	invariant(categorySlug, "categorySlug can't be empty");
	const selectedCountry = await CountryCurrencyCache.getCountryBySlug(request, countrySlug);
	const selectedCategory = await CountryCurrencyCache.getDisplayCategoryBySlug(request, categorySlug);
	invariant(selectedCountry, "country can't be empty");
	invariant(selectedCategory, "displayCategory can't be empty");
	const catalogItem = await NewCache.getCatalogItemBySlug(request, selectedCountry.countryCode, selectedCategory.displayCategory, brandSlug);
	if (!catalogItem) {
		const statusText = `Catalog item for Country: ${selectedCountry.countryCode}, Category: ${DisplayCategory[selectedCategory.displayCategory]}, Brand slug: ${brandSlug}`;
		throw json(null, { status: 401, statusText });
	}
	const headers = new Headers();
	const userPrefs = await getUserPrefs(request, headers);

	const i18nServer = await createI18nServer(request);
	const lang = await i18nServer.getLocale(request);
	const user = await authenticator.getUser(request);
	logger.warn(`User: ${user ? "exists" : "dees not exists"}`);
	const currencyCode = userPrefs?.chosenCurrencyCode ?? user?.homeCurrencyCode ?? userPrefs.detectedCurrencyCode;
	const remix = new RemixFetch(request);
	const products = await fetchSkus(remix, selectedCountry.countryCode, selectedCategory.displayCategory, catalogItem.operatorId, currencyCode, lang);
	return json({ products, selectedCountry, selectedCategory, catalogItem }, { headers });
}

export async function action({ request, params }: ActionFunctionArgs) {
	const formData = await request.formData();
	const skuId = formData.get("skuId");
	if (!skuId) return;
	const remix = new RemixFetch(request);
	const userPrefs = await getUserPrefs(request, remix.headers);
	const user = await authenticator.getUser(request);
	const currencyCode = userPrefs.chosenCurrencyCode ?? user?.homeCurrencyCode ?? userPrefs.detectedCurrencyCode;
	const cartId = await cartStorage.getCartId(request);
	const cart = await createCart(remix, skuId.toString(), currencyCode, user ? undefined : cartId)
	logger.warn(`User: ${user ? "exists" : "dees not exists"}`);
	if (user) {
		logger.info("Removed cartId for user");
		await cartStorage.removeCartId(remix.headers);
	} else {
		logger.info("Added cartId for user");
		await cartStorage.setCartId(cart.cartId, remix.headers);
	}
	return redirect(appendLangToUrl("/cart", params), { headers: remix.headers });
}

export default function CountryCategoryBrand() {
	const { t } = useTranslation();
	const { t: tCategory } = useTranslation("category");
	const { getCountryImageUrl, getOperatorImageUrl } = useUrlGenerator();
	const { products, selectedCountry, catalogItem, selectedCategory } = useLoaderData<typeof loader>();
	invariant(selectedCountry, "selectedCountry must be defined");
	invariant(selectedCategory, "selectedCategory must be defined");
	return <>
		<div className="bg-orange-50 lg:-mx-24 p-8 flex gap-10 justify-center">
			<div className="hidden lg:block w-40 bg-white p-4 rounded-xl -rotate-12">
				<img className="rounded-lg" src={getCountryImageUrl(selectedCountry.countryCode, "4x3")} />
			</div>
			<div>
				<div className="flex gap-2">
					<LangLink className="truncate" to={`/shop/${selectedCountry.slug}`}>{selectedCountry.countryName}</LangLink>
					<span>{">"}</span>
					<LangLink className="truncate" to={`/shop/${selectedCountry.slug}/${selectedCategory.slug}`}>{DisplayCategory[selectedCategory.displayCategory]}</LangLink>
					{/* <span className="truncate">{DisplayCategory[selectedCategory.displayCategory]}</span> */}
					<span>{">"}</span>
					<span className="truncate">{catalogItem.name}</span>
				</div>
				<div className="text-2xl font-bold my-4">
					{t("messages.skus_header", {
						category: tCategory(selectedCategory.displayCategoryName as any),
						operator: catalogItem.name,
						country: selectedCountry.countryName
					})}
				</div>
				<div className="flex flex-col lg:flex-row gap-x-6 gap-y-2">
					<div className="flex gap-2">
						<div className="bg-white rounded-full px-1">
							<CheckMarkIcon className="text-xl" />
						</div>
						<div>{t("skus.fast_easy_secure")}</div>
					</div>
					<div className="flex gap-2">
						<div className="bg-white rounded-full px-1">
							<CheckMarkIcon className="text-xl" />
						</div>
						<div>{t("skus.money_back")}</div>
					</div>
					<div className="flex gap-2">
						<div className="bg-white rounded-full px-1">
							<CheckMarkIcon className="text-xl" />
						</div>
						<div>{t("skus.best_price")}</div>
					</div>
				</div>
			</div>
			<div className="hidden lg:block w-40 bg-white p-4 rounded-xl -rotate-12">
				<img className="rounded-lg" style={{ maxHeight: 120 }} src={getOperatorImageUrl(catalogItem.imageName)} />
			</div>
		</div>
		<div className="mt-8 mb-4 font-medium text-lg">
			<p>{t("skus.select_denomination")}</p>
		</div>
		<div className="px-2 lg:px-6">
			<TabGroup className="mb-10">
				<div className="flex justify-center">
					<TabList className="text-center bg-gray-100 rounded-3xl p-1">
						{products.map(p => <Tab key={p.productId} className="px-7 py-2 data-[selected]:bg-white data-[selected]:font-bold data-[hover]:bg-white/70 rounded-3xl">{Category[p.category]}</Tab>)}
					</TabList>
				</div>
				<TabPanels className="pt-8">
					{products.map(p => <TabPanel key={p.productId}>
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
							{p.skus.map(s => <Form method="POST" key={s.skuId} className="bg-sky-50 px-6 py-4 w-full rounded-2xl shadow">
								{p.category === Category.Data ? <DataCard s={s} p={p} catalogItem={catalogItem} /> :
									p.category === Category.Bundle ? <BundleCard s={s} p={p} catalogItem={catalogItem} /> :
										<TopupCard s={s} p={p} catalogItem={catalogItem} />
								}
							</Form>)}
						</div>
					</TabPanel>)}
				</TabPanels>
			</TabGroup>
		</div>
	</>
}


// function concatHeaders(...headers: Headers[]) {
// 	const allHeaders = new Headers();
// 	for (const header of headers) {
// 		const cookies = header.getSetCookie();
// 		for (const cookie of cookies) {
// 			allHeaders.append("Set-Cookie", cookie);
// 		}
// 	}
// 	return allHeaders;
// }